import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import TableLayout from "../../../components/TableLayout";

import { Link } from "react-router-dom";
import ConfirmationPop from "../../../components/Modals/ConfirmationPop";
import dataHandler from "../../../hooks/dataHandler";
import {
  DELETE_QUESTION,
  QUESTION_LIST,
  REORDER_QUESTION,
} from "../../../services/ApiCalls";
import TableActions from "../../../components/Common/TableActions";
import moment from "moment";
import Filter from "../../../components/Common/Filter";
import { activeInactiveOptions } from "../../../utilities/const";
import {
  catchAsync,
  checkResponse,
  removeUnderScoreAndCapitalizeFirstLetter,
} from "../../../utilities/utilities";

// Las preguntas de una categoría son pocas: se muestran todas juntas (sin
// paginar) y en el mismo orden en que las ve el paciente en la app.
const SHOW_ALL_LIMIT = 1000;

// ícono de "agarrar" (seis puntos) para arrastrar filas
const GripIcon = () => (
  <svg width="10" height="16" viewBox="0 0 10 16" aria-hidden="true">
    {[3, 8, 13].map((y) =>
      [2.5, 7.5].map((x) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r="1.5" fill="currentColor" />
      ))
    )}
  </svg>
);

const ManageService = ({ category }) => {
  const [saving, setSaving] = useState(false);
  const dragIndexRef = useRef(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const {
    setBody,
    body,
    data,
    setData,
    loader,
    deleteModel,
    setDeleteModel,
    searchHandler,
    total,
    refetch,
    deleteHandler,
  } = dataHandler({
    extraBody: {
      serviceType: category,
      orderBy: "sortOrder",
      order: 1,
      limit: SHOW_ALL_LIMIT,
    },
    api: QUESTION_LIST,
  });

  useEffect(() => {
    setBody((p) => ({ ...p, serviceType: category }));
  }, [category]);

  // Con un filtro activo la lista está incompleta y no se puede reordenar.
  const isFiltered = !!(body.status || body.search?.trim());

  // Mueve la pregunta de la posición `from` a `to` y guarda el orden completo.
  // Lo usan tanto las flechas como el drag and drop.
  const moveHandler = catchAsync(
    async (from, to) => {
      if (!data || from === to || to < 0 || to >= data.length) return;

      const newData = [...data];
      const [moved] = newData.splice(from, 1);
      newData.splice(to, 0, moved);
      setData(newData);
      setSaving(true);

      const res = await REORDER_QUESTION({
        categoryId: category,
        ids: newData.map((item) => item._id),
      });
      if (!checkResponse({ res })) refetch();
      setSaving(false);
    },
    null,
    () => {
      setSaving(false);
      refetch();
    }
  );

  const canReorder = !isFiltered && !saving;

  const resetDrag = () => {
    dragIndexRef.current = null;
    setDragIndex(null);
    setOverIndex(null);
  };

  // El arrastre empieza solo desde el ícono de agarre, pero se arrastra la fila entera.
  const dragStartHandler = (e, index) => {
    dragIndexRef.current = index;
    setDragIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index)); // Firefox lo exige
    const row = e.currentTarget.closest("tr");
    if (row) e.dataTransfer.setDragImage(row, 20, row.offsetHeight / 2);
  };

  const rowProps = (item, index) => ({
    onDragOver: (e) => {
      if (dragIndexRef.current === null) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (overIndex !== index) setOverIndex(index);
    },
    onDrop: (e) => {
      if (dragIndexRef.current === null) return;
      e.preventDefault();
      const from = dragIndexRef.current;
      resetDrag();
      moveHandler(from, index);
    },
    style: {
      opacity: dragIndex === index ? 0.4 : 1,
      background:
        overIndex === index && dragIndex !== index ? "#e7f1ff" : undefined,
    },
  });

  const questionscolumn = [
    {
      head: "#",
      accessor: "sortOrder",
      // sin filtros la fila es la posición real; filtrada, se usa la guardada
      component: (item, key) => <>{isFiltered ? item.sortOrder : key + 1}</>,
    },
    {
      head: "Order",
      accessor: "order",
      component: (item, key) => (
        <div className="d-flex align-items-center gap-1">
          <span
            draggable={canReorder}
            onDragStart={(e) => dragStartHandler(e, key)}
            onDragEnd={resetDrag}
            title={canReorder ? "Drag to reorder" : undefined}
            className="d-inline-flex align-items-center justify-content-center px-2 text-muted"
            style={{
              cursor: canReorder ? "grab" : "not-allowed",
              opacity: canReorder ? 1 : 0.4,
              height: 31,
              userSelect: "none",
            }}
          >
            <GripIcon />
          </span>
          <Button
            size="sm"
            variant="outline-secondary"
            title="Move up"
            disabled={!canReorder || key === 0}
            onClick={() => moveHandler(key, key - 1)}
          >
            ↑
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            title="Move down"
            disabled={!canReorder || key === data.length - 1}
            onClick={() => moveHandler(key, key + 1)}
          >
            ↓
          </Button>
        </div>
      ),
    },
    { head: "Question", accessor: "name" },
    {
      head: "Created At",
      accessor: "createdAt",
      component: (item, key, arr) => (
        <>{moment(item.date_created_utc).format("DD-MM-YYYY")}</>
      ),
    },
    {
      head: "Status",
      accessor: "status",
      component: (item, key) => {
        return <p>{removeUnderScoreAndCapitalizeFirstLetter(item.status)}</p>;
      },
    },
    {
      head: "Action",
      accessor: "Action",
      component: (item, key, arr) => {
        return (
          <TableActions
            editUrl={`/question/edit/${item._id}/${category}`}
            // viewLink={`/question/detail/${item._id}`}
            setDeleteModel={() =>
              setDeleteModel({ show: true, dumpId: item?._id })
            }
          />
        );
      },
    },
  ];

  return (
    <>
      <ConfirmationPop
        confirmation={deleteModel.show}
        setConfirmation={() => setDeleteModel({ show: false, dumpId: "" })}
        type={"delete"}
        confirmHandler={() =>
          deleteHandler(() => DELETE_QUESTION({ _id: deleteModel.dumpId }))
        }
      />
      <section className="manageServiceCategory py-3 position-relative">
        <Container>
          <Row>
            <Col lg="12">
              <h4 className="mb-0 py-3 fw-bold themeBlue text-capitalize">
                Questions List
              </h4>
              <p className="text-muted mb-0">
                Questions are shown to the patient in this order. Drag a row by
                its handle or use the arrows to change it.
                {isFiltered && " Clear the filters to reorder."}
              </p>
            </Col>
            <Col lg="12" className="my-2">
              <div className="tableFilter d-flex align-items-center justify-content-between flex-wrap gap-10 mb-3">
                <div className="left">
                  <Filter
                    body={body}
                    searchHandler={searchHandler}
                    setBody={setBody}
                    statusFilterOptionArr={activeInactiveOptions}
                  />
                </div>
                <div className="right">
                  <ul className="list-unstyled ps-0 mb-0 d-flex align-items-center gap-10 flex-wrap">
                    <li className="">
                      <Link
                        to={`/question/add/${category}`}
                        className="d-flex btn btn-primary align-items-center justify-content-center fw-sbold commonBtn "
                        style={{ height: 40, minWidth: 100, fontSize: 12 }}
                      >
                        Add New
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </Col>
            <Col lg="12" className="my-2">
              <TableLayout
                column={questionscolumn}
                data={data}
                loader={loader}
                rowProps={rowProps}
              />
              {!!total && (
                <p className="m-0 fw-sbold text-dark">
                  {total} {total === 1 ? "question" : "questions"}
                </p>
              )}
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default ManageService;
